/* Custom storefront components for this theme.
   Each is a self-registering custom element so sections can drop the tag in
   without wiring any init code. */

(function () {
  'use strict';

  var MS_DAY = 86400000;

  function formatDate(date, locale) {
    try {
      return new Intl.DateTimeFormat(locale || 'en', { month: 'short', day: 'numeric' }).format(date);
    } catch (e) {
      return date.toDateString().slice(4, 10);
    }
  }

  /* The store's policies promise business days, so the timeline counts them:
     weekends are skipped, public holidays are not (the FAQ says "roughly"). */
  function addBusinessDays(days) {
    var date = new Date();
    var remaining = days;
    while (remaining > 0) {
      date.setDate(date.getDate() + 1);
      var weekday = date.getDay();
      if (weekday !== 0 && weekday !== 6) remaining--;
    }
    return date;
  }

  function withSeparators(value, decimals) {
    var parts = value.toFixed(decimals).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }

  function formatMoney(cents, format) {
    var value = cents / 100;
    return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, name) {
      switch (name) {
        case 'amount_no_decimals':
          return withSeparators(value, 0);
        case 'amount_with_comma_separator':
          return withSeparators(value, 2).replace(/[,.]/g, function (c) {
            return c === ',' ? '.' : ',';
          });
        case 'amount_no_decimals_with_comma_separator':
          return withSeparators(value, 0).replace(/,/g, '.');
        default:
          return withSeparators(value, 2);
      }
    });
  }

  /* ---------------------------------------------------------------------------
     <shipping-estimate>  Order -> shipped -> delivered timeline with live dates.
     Computed client-side so Shopify's full-page cache can't serve a stale "today";
     the Liquid fallback dates are overwritten as soon as this runs.
  --------------------------------------------------------------------------- */
  class ShippingEstimate extends HTMLElement {
    connectedCallback() {
      var locale = this.dataset.locale || 'en';
      var num = (key, fallback) => {
        var v = parseInt(this.dataset[key], 10);
        return isNaN(v) ? fallback : v;
      };

      var range = (minDays, maxDays) => {
        var a = formatDate(addBusinessDays(minDays), locale);
        if (maxDays <= minDays) return a;
        return a + ' - ' + formatDate(addBusinessDays(maxDays), locale);
      };

      var shipMin = num('shipMin', 1);
      var shipMax = num('shipMax', 2);
      var delMin = num('deliverMin', 6);
      var delMax = num('deliverMax', 11);

      this.setStep('ordered', formatDate(new Date(), locale));
      this.setStep('shipped', range(shipMin, shipMax));
      this.setStep('delivered', range(delMin, delMax));
    }

    setStep(step, text) {
      var el = this.querySelector('[data-step="' + step + '"]');
      if (el) el.textContent = text;
    }
  }
  if (!customElements.get('shipping-estimate')) {
    customElements.define('shipping-estimate', ShippingEstimate);
  }

  /* ---------------------------------------------------------------------------
     <countdown-timer>  Evergreen urgency timer.
     Each visitor gets their own deadline, persisted in localStorage so the number
     doesn't reset on every page view. Falls back to a per-session deadline when
     storage is unavailable (private windows, blocked site data).
  --------------------------------------------------------------------------- */
  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      var minutes = parseInt(this.dataset.minutes, 10);
      if (isNaN(minutes) || minutes <= 0) minutes = 30;

      this.deadline = this.resolveDeadline(minutes);
      this.parts = {
        hours: this.querySelector('[data-unit="hours"]'),
        minutes: this.querySelector('[data-unit="minutes"]'),
        seconds: this.querySelector('[data-unit="seconds"]')
      };

      this.tick();
      this.interval = setInterval(this.tick.bind(this), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.interval);
    }

    resolveDeadline(minutes) {
      var key = 'cozy-countdown:' + (this.dataset.key || 'default');
      var span = minutes * 60000;
      var now = Date.now();

      try {
        var stored = parseInt(localStorage.getItem(key), 10);
        if (!isNaN(stored) && stored > now) return stored;
        var fresh = now + span;
        localStorage.setItem(key, String(fresh));
        return fresh;
      } catch (e) {
        return now + span;
      }
    }

    tick() {
      var remaining = Math.max(0, this.deadline - Date.now());
      var total = Math.floor(remaining / 1000);

      this.write('hours', Math.floor(total / 3600));
      this.write('minutes', Math.floor((total % 3600) / 60));
      this.write('seconds', total % 60);

      if (remaining <= 0) clearInterval(this.interval);
    }

    write(unit, value) {
      var el = this.parts[unit];
      if (el) el.textContent = value < 10 ? '0' + value : String(value);
    }
  }
  if (!customElements.get('countdown-timer')) {
    customElements.define('countdown-timer', CountdownTimer);
  }

  /* ---------------------------------------------------------------------------
     <sticky-atc>  Compact buy bar that appears once the real buy button
     scrolls out of view. Clicking it forwards to the real form's submit button
     so variant state and cart-drawer behaviour stay in one place. Price, image
     and availability follow Dawn's variant-change event, because product-info.js
     only re-renders its own price/sku/inventory nodes.
  --------------------------------------------------------------------------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      // Dawn animates .product__info-wrapper with a transform, and a transformed
      // ancestor becomes the containing block for position:fixed -- which pins this
      // bar inside the product column instead of spanning the viewport. Move to
      // <body> once so no ancestor can capture it. The move re-fires this callback.
      if (!this.relocated && this.parentElement && this.parentElement !== document.body) {
        this.relocated = true;
        // A section re-render (Theme Editor edits, combined-listing swaps) inserts
        // a fresh bar while the previous one is still parked in <body>. Drop the
        // old one first so bars never stack.
        var section = this.dataset.section;
        if (section) {
          var self = this;
          document.querySelectorAll('body > sticky-atc[data-section="' + section + '"]').forEach(function (old) {
            if (old !== self) old.remove();
          });
        }
        document.body.appendChild(this);
        return;
      }

      this.target = document.querySelector(this.dataset.watch || '');
      this.button = this.querySelector('[data-sticky-submit]');

      if (this.button) {
        this.button.addEventListener('click', this.forwardClick.bind(this));
      }

      if (typeof window.subscribe === 'function') {
        this.unsubscribeVariant = window.subscribe('variant-change', this.onVariantChange.bind(this));
      }

      if (!this.target) {
        // No anchor to watch: leave the bar hidden rather than pinning it forever.
        return;
      }

      // A scroll listener rather than IntersectionObserver: the submit button sits
      // in a subtree that never reports intersections in some browsers, so an
      // observer on it silently never fires. Reading the rect on scroll is cheap
      // enough here and behaves identically everywhere.
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.onScroll();
    }

    onScroll() {
      // The anchor is replaced wholesale when Dawn re-renders the product form, so
      // re-resolve it rather than keep reading a detached node.
      if (!this.target || !this.target.isConnected) {
        this.target = document.querySelector(this.dataset.watch || '');
        if (!this.target) {
          this.classList.remove('is-visible');
          return;
        }
      }
      // Deliberately synchronous. A requestAnimationFrame guard latches forever if
      // the first frame is scheduled while the page isn't compositing (opened in a
      // background tab), which left the bar permanently dead. One rect read per
      // scroll event is cheap, and the listener is passive.
      var rect = this.target.getBoundingClientRect();
      // Visible once the real button has scrolled off the top of the viewport.
      this.classList.toggle('is-visible', rect.bottom < 0);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.unsubscribeVariant) this.unsubscribeVariant();
    }

    onVariantChange(event) {
      var variant = event && event.data && event.data.variant;
      var amount = this.querySelector('[data-sticky-amount]');
      var compare = this.querySelector('[data-sticky-compare]');
      var media = this.querySelector('[data-sticky-media]');

      if (!variant) {
        if (this.button) {
          this.button.disabled = true;
          this.button.textContent = this.dataset.labelUnavailable || 'Unavailable';
        }
        return;
      }

      var format = this.dataset.moneyFormat;
      if (amount) amount.textContent = formatMoney(variant.price, format);
      if (compare) {
        var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        compare.hidden = !onSale;
        if (onSale) compare.textContent = formatMoney(variant.compare_at_price, format);
      }
      if (media && variant.featured_image && variant.featured_image.src) {
        var src = variant.featured_image.src;
        media.src = src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=100';
      }
      if (this.button) {
        this.button.disabled = !variant.available;
        this.button.textContent = variant.available
          ? this.dataset.labelAdd || 'Add to cart'
          : this.dataset.labelSoldOut || 'Sold out';
      }
    }

    forwardClick(event) {
      // data-watch may point at the submit button itself or at a wrapper around it.
      var realButton = null;
      if (this.target) {
        realButton = this.target.matches('[type="submit"]')
          ? this.target
          : this.target.querySelector('[type="submit"]');
      }
      if (!realButton) return;
      event.preventDefault();
      realButton.click();
    }
  }
  if (!customElements.get('sticky-atc')) {
    customElements.define('sticky-atc', StickyAtc);
  }

  /* ---------------------------------------------------------------------------
     <scroll-to-top>  Appears after the visitor scrolls past the fold.
  --------------------------------------------------------------------------- */
  class ScrollToTop extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.threshold = parseInt(this.dataset.threshold, 10) || 600;

      if (this.button) {
        this.button.addEventListener('click', function () {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      }

      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    onScroll() {
      this.classList.toggle('is-visible', window.scrollY > this.threshold);
    }
  }
  if (!customElements.get('scroll-to-top')) {
    customElements.define('scroll-to-top', ScrollToTop);
  }

  /* ---------------------------------------------------------------------------
     <promo-popup>  Timed newsletter/offer modal, shown once per visitor.
     Dismissal is remembered for a configurable number of days; so is a
     successful sign-up, which counts as the strongest possible dismissal.
  --------------------------------------------------------------------------- */
  class PromoPopup extends HTMLElement {
    connectedCallback() {
      this.key = 'cozy-promo:' + (this.dataset.key || 'default');
      this.days = parseInt(this.dataset.rememberDays, 10);
      if (isNaN(this.days)) this.days = 14;

      this.onKeydown = this.onKeydown.bind(this);
      this.addEventListener('click', (event) => {
        if (event.target === this || event.target.closest('[data-popup-close]')) {
          this.close();
        }
      });

      // The customer form posts and reloads the page. Shopify sends the visitor
      // back to the form's own anchor, so the hash tells us whether it was THIS
      // form (show the thank-you straight away) or the footer's (stay closed).
      // Either way the person is now a subscriber and must not be nagged again.
      if (this.querySelector('[data-promo-posted]')) {
        this.remember();
        if (this.dataset.formId && window.location.hash === '#' + this.dataset.formId) this.open();
        return;
      }

      if (this.wasDismissed()) return;

      var delay = parseInt(this.dataset.delay, 10);
      if (isNaN(delay)) delay = 5;
      this.timer = setTimeout(this.open.bind(this), delay * 1000);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('keydown', this.onKeydown);
      document.body.classList.remove('promo-popup-open');
    }

    wasDismissed() {
      try {
        var until = parseInt(localStorage.getItem(this.key), 10);
        return !isNaN(until) && until > Date.now();
      } catch (e) {
        // Storage blocked: show the popup rather than suppressing it silently.
        return false;
      }
    }

    remember() {
      try {
        localStorage.setItem(this.key, String(Date.now() + this.days * MS_DAY));
      } catch (e) {
        /* Nothing to persist to; the popup will show again next visit. */
      }
    }

    focusables() {
      var nodes = this.querySelectorAll('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
      return Array.prototype.filter.call(nodes, function (el) {
        return !el.disabled && el.offsetParent !== null;
      });
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      // aria-modal promises assistive tech that focus stays inside; keep it.
      var items = this.focusables();
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      var active = document.activeElement;

      if (!this.contains(active)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    open() {
      if (this.classList.contains('is-open')) return;
      this.opener = document.activeElement;
      this.classList.add('is-open');
      document.body.classList.add('promo-popup-open');
      document.addEventListener('keydown', this.onKeydown);

      // Land on the field the visitor is being asked to fill, not on "close".
      var focusable =
        this.querySelector('[autofocus]') ||
        this.querySelector('input[type="email"]') ||
        this.querySelector('input, button');
      if (focusable) focusable.focus();
    }

    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('promo-popup-open');
      document.removeEventListener('keydown', this.onKeydown);
      this.remember();
      if (this.opener && typeof this.opener.focus === 'function' && this.opener !== document.body) {
        this.opener.focus();
      }
    }
  }
  if (!customElements.get('promo-popup')) {
    customElements.define('promo-popup', PromoPopup);
  }

  /* ---------------------------------------------------------------------------
     <bundle-tiers>  Quantity-tier picker that drives the product form's quantity.
     Prices are recomputed here on variant change because product-info.js only
     re-renders the price, sku, inventory and volume nodes.
  --------------------------------------------------------------------------- */
  class BundleTiers extends HTMLElement {
    connectedCallback() {
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      try {
        this.variantPrices = JSON.parse(this.dataset.variantPrices || '{}');
      } catch (e) {
        this.variantPrices = {};
      }

      this.addEventListener('change', this.onTierChange.bind(this));

      // The stepper (when the template has one) can change the quantity behind
      // our back; mirror it so the two controls never disagree.
      this.onQuantityInput = this.onQuantityInput.bind(this);
      var input = this.quantityInput;
      if (input) input.addEventListener('change', this.onQuantityInput);

      /* Dawn announces the new variant once its section fetch resolves; a plain
         change listener would read the hidden variant id before it updates. */
      if (typeof window.subscribe === 'function') {
        this.unsubscribeVariant = window.subscribe('variant-change', function (event) {
          var variant = event && event.data && event.data.variant;
          if (!variant) return;
          this.renderPrices(variant.price, variant.compare_at_price || variant.price);
        }.bind(this));
      }

      this.refreshPrices();
    }

    disconnectedCallback() {
      if (this.unsubscribeVariant) this.unsubscribeVariant();
      var input = this.quantityInput;
      if (input) input.removeEventListener('change', this.onQuantityInput);
    }

    productForm() {
      var scope = this.closest('product-info') || document;
      return scope.querySelector('form[action*="/cart/add"]');
    }

    /* The Quantity selector block, when the template has one. Without it the
       tier would be purely decorative -- the shopper picks "Buy 4" and the cart
       receives 1 -- so fall back to a hidden quantity field inside the form. */
    get quantityInput() {
      var byId = document.getElementById(this.dataset.quantityInput);
      if (byId) return byId;

      var form = this.productForm();
      if (!form) return null;

      var hidden = form.querySelector('input[name="quantity"][data-bundle-quantity]');
      if (!hidden) {
        hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'quantity';
        hidden.value = '1';
        hidden.setAttribute('data-bundle-quantity', '');
        form.appendChild(hidden);
      }
      return hidden;
    }

    onTierChange(event) {
      var radio = event.target.closest('.bundle-tiers__radio');
      if (!radio) return;

      var card = radio.closest('.bundle-tiers__card');
      this.querySelectorAll('.bundle-tiers__card').forEach(function (el) {
        el.classList.toggle('is-selected', el === card);
      });

      var input = this.quantityInput;
      if (input) {
        this.syncing = true;
        input.value = radio.value;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        this.syncing = false;
      }
    }

    onQuantityInput(event) {
      if (this.syncing) return;
      var value = parseInt(event.target.value, 10);
      this.querySelectorAll('.bundle-tiers__card').forEach(function (card) {
        var match = parseInt(card.dataset.tier, 10) === value;
        var radio = card.querySelector('.bundle-tiers__radio');
        card.classList.toggle('is-selected', match);
        if (radio) radio.checked = match;
      });
    }

    /* Initial sync from the rendered variant map, and the fallback for a page
       where the variant-change event never fires. */
    refreshPrices() {
      var form = this.productForm();
      var idInput = form && form.querySelector('input[name="id"]');
      var prices = idInput && this.variantPrices[idInput.value];
      if (!prices) return;
      this.renderPrices(prices[0], prices[1]);
    }

    renderPrices(unit, unitCompare) {
      this.querySelectorAll('.bundle-tiers__card').forEach(
        function (card) {
          var units = parseInt(card.dataset.tier, 10) || 1;
          var total = unit * units;
          var compare = unitCompare * units;

          var priceEl = card.querySelector('[data-tier-price]');
          if (priceEl) priceEl.textContent = formatMoney(total, this.moneyFormat);

          var compareEl = card.querySelector('[data-tier-compare]');
          if (compareEl) {
            compareEl.textContent = formatMoney(compare, this.moneyFormat);
            compareEl.hidden = compare <= total;
          }

          var saveEl = card.querySelector('[data-tier-save]');
          if (saveEl) {
            var saving = compare - total;
            saveEl.textContent = saveEl.dataset.prefix
              ? saveEl.dataset.prefix + ' ' + formatMoney(saving, this.moneyFormat)
              : 'You save ' + formatMoney(saving, this.moneyFormat);
            saveEl.hidden = saving <= 0;
          }
        }.bind(this)
      );
    }
  }
  if (!customElements.get('bundle-tiers')) {
    customElements.define('bundle-tiers', BundleTiers);
  }
})();

/* ===========================================================================
   Product gallery dot pagination
   Dawn's SliderComponent only maintains the "1 / 16" counter; the dot links we
   render alongside it need their own wiring. Clicking a dot scrolls the slider,
   and scrolling the slider marks the matching dot.
   =========================================================================== */
(function () {
  function initGalleryDots(slider) {
    var dotsWrap = slider.querySelector('.slider-counter--dots');
    var list = slider.querySelector('.product__media-list');
    if (!dotsWrap || !list) return;
    if (slider.dataset.dotsBound === 'true') return;
    slider.dataset.dotsBound = 'true';

    var dots = Array.prototype.slice.call(dotsWrap.querySelectorAll('.slider-counter__link--dots'));
    if (!dots.length) return;

    function setActive(index) {
      dots.forEach(function (dot, i) {
        var on = i === index;
        dot.classList.toggle('slider-counter__link--active', on);
        if (on) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        var slide = list.children[i];
        if (!slide) return;
        list.scrollTo({ left: slide.offsetLeft - list.offsetLeft, behavior: 'smooth' });
        setActive(i);
      });
    });

    // Slides sit on a wider pitch than their own width (the grid adds a gap and
    // a peek column), so dividing by clientWidth drifts a whole slide by the end
    // of a long gallery. Pick the slide whose offset is nearest instead.
    function syncFromScroll() {
      var target = list.scrollLeft;
      var best = 0;
      var bestDistance = Infinity;
      var count = Math.min(list.children.length, dots.length);
      for (var i = 0; i < count; i++) {
        var distance = Math.abs(list.children[i].offsetLeft - list.offsetLeft - target);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      }
      setActive(best);
    }

    // A timeout throttle rather than requestAnimationFrame: rAF is suspended in
    // background tabs, which would leave the dots stale after a restore.
    var scrollTimer = null;
    list.addEventListener(
      'scroll',
      function () {
        if (scrollTimer) return;
        scrollTimer = setTimeout(function () {
          scrollTimer = null;
          syncFromScroll();
        }, 60);
      },
      { passive: true }
    );
  }

  function initAll() {
    document.querySelectorAll('.product-media-slider').forEach(initGalleryDots);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Variant switches and section re-renders swap the gallery markout
  document.addEventListener('shopify:section:load', initAll);
})();
